import type { EGoalType, Goal, IPredicate, TComparison } from '@feathr/blackbox';

export const goalTypeLabels: Record<EGoalType, string> = {
  page_view: 'URL Visit',
  form_submission: 'Form Submission',
  donation: 'Donation',
  group_entry: 'Group Entry',
  conversion_pixel: 'Conversion Pixel',
};

export function getGoalSegmentName(
  goal: Goal,
  Campaigns: { get: (id: string) => { name?: string } | undefined },
): string {
  const campaignId = goal.get('parent');
  const campaign = campaignId ? Campaigns.get(campaignId) : undefined;
  const campaignName = campaign?.name || 'Unnamed Campaign';
  const convType = goal.get('conv_type');
  return `${campaignName} - ${goalTypeLabels[convType]}`;
}

export function buildURLVisitPredicates(url: string, matchMode: TComparison): IPredicate[] {
  return [
    {
      kind: 'activity',
      attr_type: 'string',
      attr_against: 'loc_url',
      filter_type: 'all',
      comparison: matchMode,
      value: url,
      excludes: false,
      group: [],
      group_mode: 'match_all',
    },
  ];
}

export function buildFormSubmissionPredicates(formIds?: string[]): IPredicate[] {
  const basePredicates: IPredicate[] = [
    {
      kind: 'activity',
      attr_against: 'flvr',
      filter_type: 'all',
      attr_type: 'string',
      comparison: 'eq',
      value: 'form_submission',
      excludes: false,
      group: [],
      group_mode: 'match_all',
    },
    {
      kind: 'activity',
      attr_against: 'form_type',
      filter_type: 'all',
      attr_type: 'string',
      comparison: 'eq',
      value: 'feathr_native',
      excludes: false,
      group: [],
      group_mode: 'match_all',
    },
  ];

  if (formIds && formIds.length > 0) {
    basePredicates.push({
      kind: 'activity',
      attr_against: 'form_id',
      filter_type: 'all',
      attr_type: 'list',
      comparison: 'in',
      value: formIds,
      excludes: false,
      group: [],
      group_mode: 'match_all',
    });
  }

  return [
    {
      kind: 'activity',
      filter_type: 'all',
      excludes: false,
      group: basePredicates,
      group_mode: 'match_all',
    },
  ];
}

export function buildDonationFormSubmissionPredicates(formIds?: string[]): IPredicate[] {
  const basePredicates: IPredicate[] = [
    {
      kind: 'activity',
      attr_against: 'flvr',
      filter_type: 'all',
      attr_type: 'string',
      comparison: 'eq',
      value: 'form_submission',
      excludes: false,
      group: [],
      group_mode: 'match_all',
    },
    {
      kind: 'activity',
      attr_against: 'form_type',
      filter_type: 'all',
      attr_type: 'string',
      comparison: 'eq',
      value: 'donation',
      excludes: false,
      group: [],
      group_mode: 'match_all',
    },
  ];

  if (formIds && formIds.length > 0) {
    basePredicates.push({
      kind: 'activity',
      attr_against: 'form_id',
      filter_type: 'all',
      attr_type: 'list',
      comparison: 'in',
      value: formIds,
      excludes: false,
      group: [],
      group_mode: 'match_all',
    });
  }

  return [
    {
      kind: 'activity',
      filter_type: 'all',
      excludes: false,
      group: basePredicates,
      group_mode: 'match_all',
    },
  ];
}

export const goalPresets: Record<EGoalType, unknown> = {
  page_view: { attr_against: 'trgt', comparison: 'eq', value: undefined },
  group_entry: { attr_against: '', comparisons: [] },
  conversion_pixel: { attr_against: 'seg_id', comparisons: ['eq'] },
  donation: {
    group: [
      {
        kind: 'activity',
        attr_type: 'string',
        group_mode: 'match_all',
        attr_against: 'flvr',
        comparison: 'eq',
        value: 'form_submission',
      },
      {
        kind: 'activity',
        attr_type: 'string',
        group_mode: 'match_all',
        attr_against: 'form_type',
        comparison: 'eq',
        value: 'donation',
      },
    ],
  },
  form_submission: {
    group: [
      {
        kind: 'activity',
        attr_type: 'string',
        group_mode: 'match_all',
        attr_against: 'flvr',
        comparison: 'eq',
        value: 'form_submission',
      },
      {
        kind: 'activity',
        attr_type: 'string',
        group_mode: 'match_all',
        attr_against: 'form_type',
        comparison: 'eq',
        value: 'feathr_native',
      },
    ],
  },
};
